import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Car, Users, Truck, DollarSign, Clock, CheckCircle, 
  MapPin, ArrowRight, LogOut, User, Loader2, UserPlus,
  BarChart3, AlertCircle, TrendingUp, Star, Mail, XCircle,
  Shield, Eye, Calendar, Phone, IdCard, SwitchCamera, CalendarDays, X, Filter, Radio, MessageCircle, Megaphone, Trash2, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { adminAPI, driverAPI, jobAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import NotificationBell from '../components/NotificationBell';
import EnablePushButton from '../components/EnablePushButton';
import UserDashboard from './UserDashboard';
import DriverDashboard from './DriverDashboard';
import ControlTower from '../components/ControlTower';
import HubsManagement from '../components/HubsManagement';
import DisputesManagement from '../components/DisputesManagement';
import CoverageLeads from '../components/CoverageLeads';
import BulkPayoutExport from '../components/BulkPayoutExport';
import ChatButton from '../components/ChatButton';
import JobFullDetails from '../components/JobFullDetails';
import AICopilotLauncher from '../components/AICopilotLauncher';
import CommunityFeed from './CommunityFeed';
import People from './People';
import { devError } from '../lib/devLog';

const CHART_COLORS = ['#f97316', '#3b82f6', '#22c55e', '#a855f7', '#ef4444', '#14b8a6'];

// Format vehicle type for display
const formatVehicleType = (type) => {
  if (!type) return 'N/A';
  const formats = {
    'car': 'Car',
    'motorcycle': 'Motorcycle',
    'ebike': 'E-Bike',
    'bicycle': 'Bicycle',
    'van': 'Van',
    'pickup_truck': 'Pickup Truck',
    'box_truck': 'Box Truck',
    'flatbed': 'Flatbed',
    'drone': 'Drone',
    'robotaxi': 'Robotaxi'
  };
  return formats[type] || type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [viewAs, setViewAs] = useState('admin');
  const [stats, setStats] = useState({});
  const [analytics, setAnalytics] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [pendingDrivers, setPendingDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  // Driver approval states
  const [showDriverDetailDialog, setShowDriverDetailDialog] = useState(false);
  const [selectedDriverDetail, setSelectedDriverDetail] = useState(null);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [approvingDriver, setApprovingDriver] = useState(false);
  const [rejectingDriver, setRejectingDriver] = useState(false);
  const [adminDateFilter, setAdminDateFilter] = useState({ start: '', end: '' });
  const [allUsers, setAllUsers] = useState([]);
  const [showBroadcastDialog, setShowBroadcastDialog] = useState(false);
  const [broadcastContent, setBroadcastContent] = useState('');
  const [broadcastAudience, setBroadcastAudience] = useState('all');
  const [sendingBroadcast, setSendingBroadcast] = useState(false);
  const [showDmDialog, setShowDmDialog] = useState(false);
  const [dmTarget, setDmTarget] = useState(null);
  const [dmContent, setDmContent] = useState('');
  const [sendingDm, setSendingDm] = useState(false);

  const handleSendDigest = async () => {
    setSendingDigest(true);
    try {
      const response = await adminAPI.sendDigest();
      toast.success(`Digest sent to ${response.data.to}!`);
    } catch (error) {
      toast.error('Failed to send digest');
    } finally {
      setSendingDigest(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Refetch jobs when date filter changes
  useEffect(() => {
    if (adminDateFilter.start || adminDateFilter.end) {
      fetchFilteredJobs();
    }
  }, [adminDateFilter.start, adminDateFilter.end]);

  const fetchFilteredJobs = async () => {
    try {
      const params = {};
      if (adminDateFilter.start) params.start_date = adminDateFilter.start;
      if (adminDateFilter.end) params.end_date = adminDateFilter.end;
      const jobsRes = await adminAPI.getAllJobs(params);
      setJobs(jobsRes.data);
    } catch (error) {
      devError('Failed to fetch filtered jobs');
    }
  };

  const fetchData = async () => {
    try {
      const [statsRes, jobsRes, driversRes, analyticsRes, pendingRes, usersRes] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getAllJobs(),
        driverAPI.getAll(),
        adminAPI.getAnalytics().catch(() => ({ data: null })),
        adminAPI.getPendingDrivers().catch(() => ({ data: { pending_drivers: [] } })),
        adminAPI.getAllUsers().catch(() => ({ data: [] })),
      ]);
      setStats(statsRes.data);
      setJobs(jobsRes.data);
      setDrivers(driversRes.data);
      setAnalytics(analyticsRes.data);
      setPendingDrivers(pendingRes.data?.pending_drivers || []);
      setAllUsers(usersRes.data || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignDriver = async () => {
    if (!selectedJob || !selectedDriver) return;
    
    setAssigning(true);
    try {
      await jobAPI.assign(selectedJob.id, selectedDriver);
      toast.success('Driver assigned successfully!');
      setShowAssignDialog(false);
      setSelectedJob(null);
      setSelectedDriver('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign driver');
    } finally {
      setAssigning(false);
    }
  };

  const openAssignDialog = (job) => {
    setSelectedJob(job);
    setShowAssignDialog(true);
  };

  const viewDriverDetail = async (driver) => {
    try {
      const res = await adminAPI.getDriverDetails(driver.id);
      setSelectedDriverDetail(res.data);
      setShowDriverDetailDialog(true);
    } catch (error) {
      toast.error('Failed to load driver details');
    }
  };

  const handleApproveDriver = async (driverId) => {
    setApprovingDriver(true);
    try {
      await adminAPI.approveDriver(driverId);
      toast.success('Driver approved successfully!');
      setShowDriverDetailDialog(false);
      setSelectedDriverDetail(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve driver');
    } finally {
      setApprovingDriver(false);
    }
  };

  const openRejectDialog = (driver) => {
    setSelectedDriverDetail(driver);
    setRejectReason('');
    setShowRejectDialog(true);
  };

  const handleRejectDriver = async () => {
    if (!selectedDriverDetail) return;
    setRejectingDriver(true);
    try {
      await adminAPI.rejectDriver(selectedDriverDetail.id, rejectReason || 'Application did not meet requirements');
      toast.success('Driver application rejected');
      setShowRejectDialog(false);
      setShowDriverDetailDialog(false);
      setSelectedDriverDetail(null);
      setRejectReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject driver');
    } finally {
      setRejectingDriver(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      assigned: 'bg-blue-100 text-blue-800',
      accepted: 'bg-indigo-100 text-indigo-800',
      in_progress: 'bg-purple-100 text-purple-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // Prepare chart data
  const getJobsByDayData = () => {
    if (!analytics?.jobs_by_day) return [];
    return Object.entries(analytics.jobs_by_day)
      .map(([date, data]) => ({
        date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        jobs: data.count,
        revenue: data.revenue,
      }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const getJobsByTypeData = () => {
    if (!analytics?.jobs_by_type) return [];
    return Object.entries(analytics.jobs_by_type).map(([type, count]) => ({
      name: type === 'ride' ? 'Rides' : 'Deliveries',
      value: count,
    }));
  };

  const getJobsByZoneData = () => {
    if (!analytics?.jobs_by_zone) return [];
    return Object.entries(analytics.jobs_by_zone)
      .map(([zone, data]) => ({
        zone: zone === 'Unknown' ? 'Other' : zone,
        jobs: data.count,
        revenue: data.revenue,
      }))
      .sort((a, b) => {
        const order = ['Hub', 'Zone 1', 'Zone 2', 'Zone 3', 'Zone 4', 'Zone 5', 'Other'];
        return order.indexOf(a.zone) - order.indexOf(b.zone);
      });
  };

  const pendingJobs = jobs.filter(j => j.status === 'pending');
  const activeJobs = jobs.filter(j => ['assigned', 'accepted', 'in_progress'].includes(j.status));
  const completedJobs = jobs.filter(j => j.status === 'completed');
  const availableDrivers = drivers.filter(d => d.is_available);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  // If viewing as User or Driver, show that dashboard with a back-to-admin banner
  if (viewAs === 'user') {
    return (
      <div>
        <div className="bg-zinc-900 text-white px-4 py-2 flex items-center justify-between sticky top-0 z-50" data-testid="admin-view-banner">
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4 text-orange-400" />
            <span className="text-sm font-medium">Viewing as <span className="text-orange-400">User</span></span>
          </div>
          <Button size="sm" variant="secondary" className="h-7 text-xs" onClick={() => setViewAs('admin')} data-testid="back-to-admin-btn">
            <Shield className="w-3 h-3 mr-1" /> Back to Admin
          </Button>
        </div>
        <UserDashboard />
      </div>
    );
  }
  
  if (viewAs === 'driver') {
    return (
      <div>
        <div className="bg-zinc-900 text-white px-4 py-2 flex items-center justify-between sticky top-0 z-50" data-testid="admin-view-banner">
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4 text-orange-400" />
            <span className="text-sm font-medium">Viewing as <span className="text-orange-400">Driver</span></span>
          </div>
          <Button size="sm" variant="secondary" className="h-7 text-xs" onClick={() => setViewAs('admin')} data-testid="back-to-admin-btn">
            <Shield className="w-3 h-3 mr-1" /> Back to Admin
          </Button>
        </div>
        <DriverDashboard />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-40">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
                <Car className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-lg sm:text-xl tracking-tight text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>LocalMove</span>
              <span className="text-xs bg-zinc-900 text-white px-2 py-1 rounded-full ml-1">Admin</span>
            </div>
          
            <div className="flex items-center gap-2 sm:gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={handleSendDigest}
                disabled={sendingDigest}
                data-testid="send-digest-btn"
                className="hidden sm:flex"
              >
                {sendingDigest ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Mail className="w-4 h-4 mr-2" />
                )}
                {sendingDigest ? 'Sending...' : 'Send Digest'}
              </Button>
              <Select value="admin" onValueChange={(val) => setViewAs(val)}>
                <SelectTrigger className="w-[120px] sm:w-[150px] h-9" data-testid="role-switcher">
                  <SwitchCamera className="w-4 h-4 mr-1 text-orange-500" />
                  <SelectValue placeholder="View as..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin View</SelectItem>
                  <SelectItem value="user">User View</SelectItem>
                  <SelectItem value="driver">Driver View</SelectItem>
                </SelectContent>
              </Select>
              <NotificationBell />
              <ChatButton />
              <AICopilotLauncher />
              <EnablePushButton compact />
              <span className="font-medium text-zinc-600 hidden md:block">{user?.name}</span>
              <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="admin-logout-btn">
                <LogOut className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-4 sm:py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <div className="bg-white border border-zinc-200 rounded-xl p-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-zinc-500">Total Users</p>
                <p className="text-2xl font-bold text-zinc-900" data-testid="stat-users">
                  {stats.total_users || 0}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white border border-zinc-200 rounded-xl p-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <Truck className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-zinc-500">Total Drivers</p>
                <p className="text-2xl font-bold text-zinc-900" data-testid="stat-drivers">
                  {stats.total_drivers || 0}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white border border-zinc-200 rounded-xl p-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-zinc-500">Total Jobs</p>
                <p className="text-2xl font-bold text-zinc-900" data-testid="stat-jobs">
                  {stats.total_jobs || 0}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white border border-zinc-200 rounded-xl p-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-zinc-500">Total Revenue</p>
                <p className="text-2xl font-bold text-zinc-900" data-testid="stat-revenue">
                  ${(stats.total_revenue || 0).toFixed(2)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Secondary Stats */}
        <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-center gap-3">
            <Clock className="w-6 h-6 text-yellow-600" />
            <div>
              <p className="text-sm text-yellow-700">Pending Jobs</p>
              <p className="text-xl font-bold text-yellow-800">{stats.pending_jobs || 0}</p>
            </div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-green-600" />
            <div>
              <p className="text-sm text-green-700">Completed Jobs</p>
              <p className="text-xl font-bold text-green-800">{stats.completed_jobs || 0}</p>
            </div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center gap-3">
            <Truck className="w-6 h-6 text-blue-600" />
            <div>
              <p className="text-sm text-blue-700">Available Drivers</p>
              <p className="text-xl font-bold text-blue-800">{availableDrivers.length}</p>
            </div>
          </div>
        </div>

        {/* Analytics Charts */}
        {analytics && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-zinc-900 mb-4 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <TrendingUp className="w-5 h-5 text-orange-500" />
              Analytics
            </h2>
            
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Jobs Over Time */}
              <div className="bg-white border border-zinc-200 rounded-xl p-5" data-testid="chart-jobs-over-time">
                <h3 className="text-base font-medium text-zinc-900 mb-4">Jobs & Revenue Over Time</h3>
                <div className="h-64">
                  {getJobsByDayData().length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={getJobsByDayData()}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                        <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#71717a" />
                        <YAxis yAxisId="left" tick={{ fontSize: 12 }} stroke="#71717a" />
                        <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} stroke="#71717a" />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#fff', border: '1px solid #e4e4e7', borderRadius: '8px' }}
                        />
                        <Legend />
                        <Line yAxisId="left" type="monotone" dataKey="jobs" name="Jobs" stroke="#f97316" strokeWidth={2} dot={{ fill: '#f97316' }} />
                        <Line yAxisId="right" type="monotone" dataKey="revenue" name="Revenue ($)" stroke="#22c55e" strokeWidth={2} dot={{ fill: '#22c55e' }} />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-zinc-400">
                      <p>No data available</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Jobs by Type (Pie Chart) */}
              <div className="bg-white border border-zinc-200 rounded-xl p-5" data-testid="chart-jobs-by-type">
                <h3 className="text-base font-medium text-zinc-900 mb-4">Jobs by Type</h3>
                <div className="h-64">
                  {getJobsByTypeData().length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={getJobsByTypeData()}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={90}
                          fill="#8884d8"
                          paddingAngle={5}
                          dataKey="value"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        >
                          {getJobsByTypeData().map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-zinc-400">
                      <p>No data available</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Jobs by Zone (Bar Chart) */}
              <div className="bg-white border border-zinc-200 rounded-xl p-5" data-testid="chart-jobs-by-zone">
                <h3 className="text-base font-medium text-zinc-900 mb-4">Jobs by Zone</h3>
                <div className="h-64">
                  {getJobsByZoneData().length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={getJobsByZoneData()}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                        <XAxis dataKey="zone" tick={{ fontSize: 11 }} stroke="#71717a" />
                        <YAxis tick={{ fontSize: 12 }} stroke="#71717a" />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#fff', border: '1px solid #e4e4e7', borderRadius: '8px' }}
                        />
                        <Legend />
                        <Bar dataKey="jobs" name="Jobs" fill="#f97316" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="revenue" name="Revenue ($)" fill="#22c55e" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-zinc-400">
                      <p>No data available</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Top Drivers */}
              <div className="bg-white border border-zinc-200 rounded-xl p-5" data-testid="chart-top-drivers">
                <h3 className="text-base font-medium text-zinc-900 mb-4">Top Drivers</h3>
                <div className="space-y-3">
                  {analytics.driver_performance && analytics.driver_performance.length > 0 ? (
                    analytics.driver_performance.slice(0, 5).map((driver, index) => (
                      <div key={driver.id} className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                            index === 0 ? 'bg-yellow-100 text-yellow-700' :
                            index === 1 ? 'bg-zinc-200 text-zinc-700' :
                            index === 2 ? 'bg-orange-100 text-orange-700' :
                            'bg-zinc-100 text-zinc-600'
                          }`}>
                            {index + 1}
                          </div>
                          <div>
                            <p className="font-medium text-zinc-900">{driver.name}</p>
                            <div className="flex items-center gap-1 text-sm text-zinc-500">
                              <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                              <span>{driver.average_rating || 'N/A'}</span>
                              <span className="text-zinc-300">|</span>
                              <span>{driver.total_ratings} ratings</span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-zinc-900">{driver.completed_jobs}</p>
                          <p className="text-xs text-zinc-500">jobs completed</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="py-8 text-center text-zinc-400">
                      <Truck className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>No driver data available</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Jobs & Drivers Tabs */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-3">
          <h2 className="text-lg font-semibold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Jobs & Drivers</h2>
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="w-4 h-4 text-zinc-400 hidden sm:block" />
            <Input
              type="date"
              value={adminDateFilter.start}
              onChange={(e) => setAdminDateFilter(prev => ({ ...prev, start: e.target.value }))}
              className="h-8 w-[130px] text-sm"
              data-testid="admin-date-start"
            />
            <span className="text-zinc-400 text-sm">to</span>
            <Input
              type="date"
              value={adminDateFilter.end}
              onChange={(e) => setAdminDateFilter(prev => ({ ...prev, end: e.target.value }))}
              className="h-8 w-[130px] text-sm"
              data-testid="admin-date-end"
            />
            {(adminDateFilter.start || adminDateFilter.end) && (
              <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => { setAdminDateFilter({ start: '', end: '' }); fetchData(); }} data-testid="admin-clear-date">
                <X className="w-3 h-3 mr-1" /> Clear
              </Button>
            )}
          </div>
        </div>
        <Tabs defaultValue="pending" className="space-y-4 sm:space-y-6">
          <div className="relative">
            <TabsList
              className="bg-white border border-zinc-200 flex flex-nowrap justify-start overflow-x-auto w-full h-auto p-1 gap-1 scrollbar-thin scroll-smooth"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              <TabsTrigger value="pending" data-testid="admin-pending-tab" className="shrink-0 px-3 py-2 min-h-[40px] whitespace-nowrap">
                Pending ({pendingJobs.length})
              </TabsTrigger>
              <TabsTrigger value="active" data-testid="admin-active-tab" className="shrink-0 px-3 py-2 min-h-[40px] whitespace-nowrap">
                Active ({activeJobs.length})
              </TabsTrigger>
              <TabsTrigger value="completed" data-testid="admin-completed-tab" className="shrink-0 px-3 py-2 min-h-[40px] whitespace-nowrap">
                Completed ({completedJobs.length})
              </TabsTrigger>
              <TabsTrigger value="drivers" data-testid="admin-drivers-tab" className="shrink-0 px-3 py-2 min-h-[40px] whitespace-nowrap">
                Drivers ({drivers.length})
              </TabsTrigger>
              <TabsTrigger value="approvals" data-testid="admin-approvals-tab" className="shrink-0 px-3 py-2 min-h-[40px] whitespace-nowrap relative">
                <Shield className="w-4 h-4 mr-1" />
                Approvals
                {pendingDrivers.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {pendingDrivers.length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="control-tower" data-testid="admin-control-tower-tab" className="shrink-0 px-3 py-2 min-h-[40px] whitespace-nowrap">
                <Radio className="w-4 h-4 mr-1" />
                Control Tower
              </TabsTrigger>
              <TabsTrigger value="users" data-testid="admin-users-tab" className="shrink-0 px-3 py-2 min-h-[40px] whitespace-nowrap">
                <Users className="w-4 h-4 mr-1" />
                All Users
              </TabsTrigger>
              <TabsTrigger value="community" data-testid="admin-community-tab" className="shrink-0 px-3 py-2 min-h-[40px] whitespace-nowrap">
                <MessageCircle className="w-4 h-4 mr-1" />
                Community
              </TabsTrigger>
              <TabsTrigger value="people" data-testid="admin-people-tab" className="shrink-0 px-3 py-2 min-h-[40px] whitespace-nowrap">
                <User className="w-4 h-4 mr-1" />
                People
              </TabsTrigger>
              <TabsTrigger value="hubs" data-testid="admin-hubs-tab" className="shrink-0 px-3 py-2 min-h-[40px] whitespace-nowrap">
                <MapPin className="w-4 h-4 mr-1" />
                Hubs
              </TabsTrigger>
              <TabsTrigger value="disputes" data-testid="admin-disputes-tab" className="shrink-0 px-3 py-2 min-h-[40px] whitespace-nowrap">
                <AlertCircle className="w-4 h-4 mr-1" />
                Disputes
              </TabsTrigger>
              <TabsTrigger value="leads" data-testid="admin-leads-tab" className="shrink-0 px-3 py-2 min-h-[40px] whitespace-nowrap">
                <TrendingUp className="w-4 h-4 mr-1" />
                Leads
              </TabsTrigger>
            </TabsList>
            <div
              className="pointer-events-none absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent md:hidden"
              aria-hidden="true"
            />
          </div>

          {/* Pending Jobs */}
          <TabsContent value="pending" className="space-y-4">
            {pendingJobs.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-zinc-200">
                <div className="w-16 h-16 bg-zinc-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-zinc-400" />
                </div>
                <h3 className="text-lg font-semibold text-zinc-900 mb-2">No pending jobs</h3>
                <p className="text-zinc-600">All jobs have been assigned!</p>
              </div>
            ) : (
              pendingJobs.map((job) => (
                <div
                  key={job.id}
                  className="bg-white border border-zinc-200 rounded-xl p-5"
                  data-testid={`admin-job-${job.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-zinc-900 capitalize">
                          {job.job_type === 'ride' ? 'Standard Ride' : `${job.delivery_category} Delivery`}
                        </h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(job.status)}`}>
                          {job.status}
                        </span>
                      </div>
                      <p className="text-sm text-zinc-600">Customer: {job.user_name}</p>
                      <div className="flex items-center gap-2 text-sm text-zinc-500">
                        <MapPin className="w-4 h-4" />
                        <span>{job.pickup_address}</span>
                        <ArrowRight className="w-4 h-4" />
                        <span>{job.dropoff_address}</span>
                      </div>
                      <p className="text-xs text-zinc-400 font-mono">ID: {job.id.slice(0, 8)}...</p>
                    </div>
                    <div className="text-right flex items-center gap-2">
                      <div>
                        <p className="text-xl font-bold text-zinc-900">${job.price.toFixed(2)}</p>
                        <p className="text-sm text-zinc-500">{new Date(job.created_at).toLocaleString()}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-zinc-400 hover:text-red-600"
                        onClick={async () => {
                          if (!confirm('Delete this job permanently?')) return;
                          try {
                            await adminAPI.deleteJob(job.id);
                            toast.success('Job deleted');
                            fetchData();
                          } catch (e) { toast.error('Failed to delete job'); }
                        }}
                        data-testid={`delete-pending-job-${job.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <JobFullDetails job={job} variant="admin" />
                  
                  <div className="mt-4 pt-4 border-t border-zinc-100">
                    <Button
                      onClick={() => openAssignDialog(job)}
                      className="bg-orange-500 hover:bg-orange-600 text-white"
                      data-testid={`assign-driver-btn-${job.id}`}
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Assign Driver
                    </Button>
                  </div>
                </div>
              ))
            )}
          </TabsContent>

          {/* Active Jobs */}
          <TabsContent value="active" className="space-y-4">
            {activeJobs.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-zinc-200">
                <div className="w-16 h-16 bg-zinc-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="w-8 h-8 text-zinc-400" />
                </div>
                <h3 className="text-lg font-semibold text-zinc-900 mb-2">No active jobs</h3>
                <p className="text-zinc-600">No jobs currently in progress</p>
              </div>
            ) : (
              activeJobs.map((job) => (
                <div
                  key={job.id}
                  className="bg-white border border-zinc-200 rounded-xl p-5"
                >
                  <div className="flex items-start justify-between">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-zinc-900 capitalize">
                          {job.job_type === 'ride' ? 'Standard Ride' : `${job.delivery_category} Delivery`}
                        </h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(job.status)}`}>
                          {job.status.replace('_', ' ')}
                        </span>
                      </div>
                      <p className="text-sm text-zinc-600">Customer: {job.user_name}</p>
                      <p className="text-sm text-zinc-600">Driver: <span className="font-medium">{job.driver_name}</span></p>
                      <div className="flex items-center gap-2 text-sm text-zinc-500">
                        <MapPin className="w-4 h-4" />
                        <span>{job.pickup_address}</span>
                        <ArrowRight className="w-4 h-4" />
                        <span>{job.dropoff_address}</span>
                      </div>
                    </div>
                    <div className="text-right flex items-center gap-2">
                      <div>
                        <p className="text-xl font-bold text-zinc-900">${job.price.toFixed(2)}</p>
                        <p className="text-sm text-zinc-500">{new Date(job.created_at).toLocaleString()}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-zinc-400 hover:text-red-600"
                        onClick={async () => {
                          if (!confirm('Delete this job permanently?')) return;
                          try {
                            await adminAPI.deleteJob(job.id);
                            toast.success('Job deleted');
                            fetchData();
                          } catch (e) { toast.error('Failed to delete job'); }
                        }}
                        data-testid={`delete-active-job-${job.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <JobFullDetails job={job} variant="admin" />
                </div>
              ))
            )}
          </TabsContent>

          {/* Completed Jobs */}
          <TabsContent value="completed" className="space-y-4">
            {completedJobs.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-zinc-200">
                <div className="w-16 h-16 bg-zinc-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-zinc-400" />
                </div>
                <h3 className="text-lg font-semibold text-zinc-900 mb-2">No completed jobs yet</h3>
                <p className="text-zinc-600">Completed jobs will appear here</p>
              </div>
            ) : (
              completedJobs.slice(0, 10).map((job) => (
                <div
                  key={job.id}
                  className="bg-white border border-zinc-200 rounded-xl p-4 flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-zinc-900 capitalize">
                        {job.job_type === 'ride' ? 'Standard Ride' : `${job.delivery_category} Delivery`}
                      </h3>
                      <p className="text-sm text-zinc-500">
                        {job.user_name} → {job.driver_name}
                      </p>
                    </div>
                  </div>
                  <div className="text-right flex items-center gap-2">
                    <div>
                      <p className="font-bold text-green-600">${job.price.toFixed(2)}</p>
                      <p className="text-xs text-zinc-500">{new Date(job.updated_at).toLocaleDateString()}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-zinc-500 hover:text-orange-600"
                      onClick={async () => {
                        if (!confirm(`Reopen this ${job.job_type}? It will return to Pending and any driver can accept it.`)) return;
                        try {
                          await adminAPI.reopenJob(job.id);
                          toast.success('Job reopened — now in Pending');
                          fetchData();
                        } catch (e) {
                          toast.error(e.response?.data?.detail || 'Failed to reopen job');
                        }
                      }}
                      data-testid={`reopen-job-${job.id}`}
                      title="Reopen job"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-zinc-400 hover:text-red-600"
                      onClick={async () => {
                        if (!confirm('Delete this job permanently?')) return;
                        try {
                          await adminAPI.deleteJob(job.id);
                          toast.success('Job deleted');
                          fetchData();
                        } catch (e) { toast.error('Failed to delete job'); }
                      }}
                      data-testid={`delete-job-${job.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </TabsContent>

          {/* Drivers */}
          <TabsContent value="drivers" className="space-y-4">
            <BulkPayoutExport />
            {drivers.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-zinc-200">
                <div className="w-16 h-16 bg-zinc-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Truck className="w-8 h-8 text-zinc-400" />
                </div>
                <h3 className="text-lg font-semibold text-zinc-900 mb-2">No drivers yet</h3>
                <p className="text-zinc-600">Drivers will appear here once registered</p>
              </div>
            ) : (
              drivers.map((driver) => (
                <div
                  key={driver.id}
                  className="bg-white border border-zinc-200 rounded-xl p-5"
                  data-testid={`driver-card-${driver.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${driver.is_available ? 'bg-green-100' : 'bg-zinc-100'}`}>
                        <User className={`w-6 h-6 ${driver.is_available ? 'text-green-600' : 'text-zinc-400'}`} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-zinc-900">{driver.name}</h3>
                        <p className="text-sm text-zinc-500">{driver.email}</p>
                        <div className="flex items-center gap-3 mt-1 text-sm">
                          <span className="text-zinc-600">{formatVehicleType(driver.vehicle_type)}</span>
                          <span className="text-zinc-400">•</span>
                          <span className="font-mono text-zinc-600">{driver.vehicle_plate}</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right flex items-center gap-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-zinc-500 hover:text-orange-600"
                        onClick={() => { setDmTarget(driver); setShowDmDialog(true); }}
                        data-testid={`dm-driver-${driver.id}`}
                      >
                        <MessageCircle className="w-4 h-4" />
                      </Button>
                      <div>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${driver.is_available ? 'bg-green-100 text-green-800' : 'bg-zinc-100 text-zinc-600'}`}>
                          {driver.is_available ? 'Available' : 'Offline'}
                        </span>
                        <p className="text-lg font-bold text-zinc-900 mt-2">${driver.total_earnings.toFixed(2)}</p>
                        <p className="text-xs text-zinc-500">Total earnings</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </TabsContent>

          {/* Pending Driver Approvals */}
          <TabsContent value="approvals" className="space-y-4">
            {pendingDrivers.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-zinc-200">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>
                <h3 className="text-lg font-semibold text-zinc-900 mb-2">No pending applications</h3>
                <p className="text-zinc-600">All driver applications have been processed</p>
              </div>
            ) : (
              pendingDrivers.map((driver) => (
                <div
                  key={driver.id}
                  className="bg-white border border-zinc-200 rounded-xl p-5"
                  data-testid={`pending-driver-${driver.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-yellow-100 rounded-full flex items-center justify-center">
                        <User className="w-7 h-7 text-yellow-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-zinc-900">{driver.name}</h3>
                        <p className="text-sm text-zinc-500">{driver.email}</p>
                        <div className="flex items-center gap-3 mt-1 text-sm">
                          <span className="text-zinc-600">{formatVehicleType(driver.vehicle_type)}</span>
                          <span className="text-zinc-400">•</span>
                          <span className="font-mono text-zinc-600">{driver.vehicle_plate}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-2 text-xs text-zinc-500">
                          <Calendar className="w-3 h-3" />
                          <span>Applied: {new Date(driver.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => viewDriverDetail(driver)}
                        data-testid={`view-driver-${driver.id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Review
                      </Button>
                      <Button
                        size="sm"
                        className="bg-green-500 hover:bg-green-600 text-white"
                        onClick={() => handleApproveDriver(driver.id)}
                        disabled={approvingDriver}
                        data-testid={`approve-driver-${driver.id}`}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Approve
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => openRejectDialog(driver)}
                        data-testid={`reject-driver-${driver.id}`}
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Reject
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </TabsContent>

          {/* Control Tower Tab */}
          <TabsContent value="control-tower">
            <ControlTower />
          </TabsContent>

          {/* All Users Tab */}
          <TabsContent value="users" className="space-y-4">
            <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-zinc-100 flex items-center justify-between flex-wrap gap-2">
                <h3 className="font-semibold text-zinc-900">All Users ({allUsers.length})</h3>
                <div className="flex items-center gap-2">
                  <div className="flex gap-2 text-xs">
                    <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-700">{allUsers.filter(u => u.role === 'user').length} Users</span>
                    <span className="px-2 py-1 rounded-full bg-purple-100 text-purple-700">{allUsers.filter(u => u.role === 'driver').length} Drivers</span>
                    <span className="px-2 py-1 rounded-full bg-orange-100 text-orange-700">{allUsers.filter(u => u.role === 'admin').length} Admins</span>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => setShowBroadcastDialog(true)}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                    data-testid="broadcast-btn"
                  >
                    <Megaphone className="w-4 h-4 mr-1" />
                    Broadcast
                  </Button>
                </div>
              </div>
              <div className="divide-y divide-zinc-100 max-h-[600px] overflow-y-auto">
                {allUsers.map((u) => (
                  <div key={u.id} className="p-4 flex items-center justify-between hover:bg-zinc-50" data-testid={`user-row-${u.id}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                        u.role === 'admin' ? 'bg-orange-500' : u.role === 'driver' ? 'bg-purple-500' : 'bg-blue-500'
                      }`}>
                        {u.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <p className="font-medium text-zinc-900 text-sm">{u.name || 'No name'}</p>
                        <p className="text-xs text-zinc-500">{u.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3 text-sm">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-zinc-500 hover:text-orange-600"
                        onClick={() => { setDmTarget(u); setShowDmDialog(true); }}
                        data-testid={`dm-user-${u.id}`}
                      >
                        <MessageCircle className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-zinc-400 hover:text-red-600"
                        onClick={async () => {
                          if (!confirm(`Delete ${u.name || u.email}? This removes all their data.`)) return;
                          try {
                            await adminAPI.deleteUser(u.id);
                            toast.success(`${u.email} deleted`);
                            fetchData();
                          } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete user'); }
                        }}
                        data-testid={`delete-user-${u.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        u.role === 'admin' ? 'bg-orange-100 text-orange-700' :
                        u.role === 'driver' ? 'bg-purple-100 text-purple-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {u.role}
                      </span>
                      {u.role === 'driver' && (
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium hidden sm:inline ${
                          u.driver_status === 'approved' ? 'bg-green-100 text-green-700' :
                          u.driver_status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {u.driver_status || 'approved'}
                        </span>
                      )}
                      <span className="text-xs text-zinc-400 hidden sm:block">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : ''}
                      </span>
                    </div>
                  </div>
                ))}
                {allUsers.length === 0 && (
                  <div className="p-8 text-center text-zinc-400">No users found</div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Community Feed Tab */}
          <TabsContent value="community">
            <CommunityFeed embedded />
          </TabsContent>

          {/* People Tab */}
          <TabsContent value="people">
            <People embedded />
          </TabsContent>

          {/* Hubs Tab */}
          <TabsContent value="hubs">
            <HubsManagement />
          </TabsContent>

          {/* Disputes Tab */}
          <TabsContent value="disputes">
            <DisputesManagement />
          </TabsContent>

          {/* Coverage Leads Tab */}
          <TabsContent value="leads">
            <CoverageLeads />
          </TabsContent>
        </Tabs>
      </main>

      {/* Assign Driver Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Assign Driver</DialogTitle>
            <DialogDescription>
              Select a driver to assign to this job
            </DialogDescription>
          </DialogHeader>
          
          {selectedJob && (
            <div className="space-y-4">
              <div className="bg-zinc-50 rounded-lg p-4">
                <p className="font-medium text-zinc-900 capitalize">
                  {selectedJob.job_type === 'ride' ? 'Standard Ride' : `${selectedJob.delivery_category} Delivery`}
                </p>
                <p className="text-sm text-zinc-600 mt-1">Customer: {selectedJob.user_name}</p>
                <p className="text-sm text-zinc-600">${selectedJob.price.toFixed(2)}</p>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-zinc-700">Select Driver</label>
                <Select value={selectedDriver} onValueChange={setSelectedDriver}>
                  <SelectTrigger data-testid="driver-select">
                    <SelectValue placeholder="Choose a driver" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableDrivers.length === 0 ? (
                      <SelectItem value="none" disabled>No available drivers</SelectItem>
                    ) : (
                      availableDrivers.map((driver) => (
                        <SelectItem key={driver.id} value={driver.id}>
                          {driver.name} - {formatVehicleType(driver.vehicle_type)}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setShowAssignDialog(false)} className="flex-1">
                  Cancel
                </Button>
                <Button
                  onClick={handleAssignDriver}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                  disabled={!selectedDriver || assigning}
                  data-testid="confirm-assign-btn"
                >
                  {assigning ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Assigning...
                    </>
                  ) : (
                    'Assign Driver'
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Driver Detail Dialog */}
      <Dialog open={showDriverDetailDialog} onOpenChange={setShowDriverDetailDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }} className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-orange-500" />
              Driver Application Review
            </DialogTitle>
            <DialogDescription>
              Review the applicant's information before approving or rejecting
            </DialogDescription>
          </DialogHeader>
          
          {selectedDriverDetail && (
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="bg-zinc-50 rounded-lg p-4">
                <h4 className="font-semibold text-zinc-900 mb-3 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Basic Information
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-zinc-500">Full Name:</span>
                    <p className="font-medium text-zinc-900">{selectedDriverDetail.name}</p>
                  </div>
                  <div>
                    <span className="text-zinc-500">Email:</span>
                    <p className="font-medium text-zinc-900">{selectedDriverDetail.email}</p>
                  </div>
                  <div>
                    <span className="text-zinc-500">Phone:</span>
                    <p className="font-medium text-zinc-900">{selectedDriverDetail.phone}</p>
                  </div>
                  <div>
                    <span className="text-zinc-500">Date of Birth:</span>
                    <p className="font-medium text-zinc-900">{selectedDriverDetail.date_of_birth || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Vehicle Info */}
              <div className="bg-zinc-50 rounded-lg p-4">
                <h4 className="font-semibold text-zinc-900 mb-3 flex items-center gap-2">
                  <Truck className="w-4 h-4" />
                  Vehicle Information
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-zinc-500">Vehicle Type:</span>
                    <p className="font-medium text-zinc-900">{formatVehicleType(selectedDriverDetail.vehicle_type)}</p>
                  </div>
                  <div>
                    <span className="text-zinc-500">License Plate:</span>
                    <p className="font-medium text-zinc-900 font-mono">{selectedDriverDetail.vehicle_plate}</p>
                  </div>
                </div>
              </div>

              {/* License Info */}
              <div className="bg-zinc-50 rounded-lg p-4">
                <h4 className="font-semibold text-zinc-900 mb-3 flex items-center gap-2">
                  <IdCard className="w-4 h-4" />
                  Driver's License
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-zinc-500">License Number:</span>
                    <p className="font-medium text-zinc-900 font-mono">{selectedDriverDetail.drivers_license || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-zinc-500">State:</span>
                    <p className="font-medium text-zinc-900">{selectedDriverDetail.license_state || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-zinc-500">Expiry Date:</span>
                    <p className="font-medium text-zinc-900">{selectedDriverDetail.license_expiry || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="bg-zinc-50 rounded-lg p-4">
                <h4 className="font-semibold text-zinc-900 mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Address
                </h4>
                <div className="text-sm">
                  <p className="font-medium text-zinc-900">
                    {selectedDriverDetail.address && `${selectedDriverDetail.address}, `}
                    {selectedDriverDetail.city && `${selectedDriverDetail.city}, `}
                    {selectedDriverDetail.state && `${selectedDriverDetail.state} `}
                    {selectedDriverDetail.zip_code || ''}
                  </p>
                  {!selectedDriverDetail.address && <p className="text-zinc-500">No address provided</p>}
                </div>
              </div>

              {/* References */}
              <div className="bg-zinc-50 rounded-lg p-4">
                <h4 className="font-semibold text-zinc-900 mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  References
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-zinc-500">Reference 1:</span>
                    <p className="font-medium text-zinc-900">
                      {selectedDriverDetail.reference1_name || 'Not provided'}
                    </p>
                    {selectedDriverDetail.reference1_phone && (
                      <p className="text-zinc-600">{selectedDriverDetail.reference1_phone}</p>
                    )}
                  </div>
                  <div>
                    <span className="text-zinc-500">Reference 2:</span>
                    <p className="font-medium text-zinc-900">
                      {selectedDriverDetail.reference2_name || 'Not provided'}
                    </p>
                    {selectedDriverDetail.reference2_phone && (
                      <p className="text-zinc-600">{selectedDriverDetail.reference2_phone}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Consent */}
              <div className={`rounded-lg p-4 ${selectedDriverDetail.background_check_consent ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <div className="flex items-center gap-2">
                  {selectedDriverDetail.background_check_consent ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-600" />
                  )}
                  <span className={`font-medium ${selectedDriverDetail.background_check_consent ? 'text-green-800' : 'text-red-800'}`}>
                    Background Check Consent: {selectedDriverDetail.background_check_consent ? 'Granted' : 'Not Granted'}
                  </span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowDriverDetailDialog(false)}
                >
                  Close
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={() => {
                    setShowDriverDetailDialog(false);
                    openRejectDialog(selectedDriverDetail);
                  }}
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject
                </Button>
                <Button
                  className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                  onClick={() => handleApproveDriver(selectedDriverDetail.id)}
                  disabled={approvingDriver}
                  data-testid="approve-driver-detail-btn"
                >
                  {approvingDriver ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Approving...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Approve Driver
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Driver Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }} className="flex items-center gap-2 text-red-600">
              <XCircle className="w-5 h-5" />
              Reject Application
            </DialogTitle>
            <DialogDescription>
              Provide a reason for rejecting this driver application
            </DialogDescription>
          </DialogHeader>
          
          {selectedDriverDetail && (
            <div className="space-y-4">
              <div className="bg-zinc-50 rounded-lg p-4">
                <p className="font-medium text-zinc-900">{selectedDriverDetail.name}</p>
                <p className="text-sm text-zinc-500">{selectedDriverDetail.email}</p>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-zinc-700">Rejection Reason</label>
                <Textarea
                  placeholder="e.g., Incomplete documentation, license expired, etc."
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  rows={3}
                  data-testid="reject-reason-input"
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowRejectDialog(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={handleRejectDriver}
                  disabled={rejectingDriver}
                  data-testid="confirm-reject-btn"
                >
                  {rejectingDriver ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Rejecting...
                    </>
                  ) : (
                    'Reject Application'
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Broadcast Message Dialog */}
      <Dialog open={showBroadcastDialog} onOpenChange={setShowBroadcastDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Megaphone className="w-5 h-5 text-orange-500" /> Broadcast Message
            </DialogTitle>
            <DialogDescription>Send a message to multiple users at once</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Audience</Label>
              <Select value={broadcastAudience} onValueChange={setBroadcastAudience}>
                <SelectTrigger data-testid="broadcast-audience">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users & Drivers</SelectItem>
                  <SelectItem value="users">Users Only</SelectItem>
                  <SelectItem value="drivers">Drivers Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Message</Label>
              <Textarea
                value={broadcastContent}
                onChange={(e) => setBroadcastContent(e.target.value)}
                placeholder="Type your broadcast message..."
                className="min-h-[100px]"
                data-testid="broadcast-content"
              />
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => { setShowBroadcastDialog(false); setBroadcastContent(''); }}>Cancel</Button>
              <Button
                className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                disabled={!broadcastContent.trim() || sendingBroadcast}
                onClick={async () => {
                  setSendingBroadcast(true);
                  try {
                    const res = await adminAPI.broadcastMessage(broadcastContent.trim(), broadcastAudience);
                    toast.success(`Broadcast sent to ${res.data.recipients} recipients`);
                    setShowBroadcastDialog(false);
                    setBroadcastContent('');
                  } catch (err) {
                    toast.error('Failed to send broadcast');
                  } finally {
                    setSendingBroadcast(false);
                  }
                }}
                data-testid="send-broadcast-btn"
              >
                {sendingBroadcast ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Megaphone className="w-4 h-4 mr-1" />}
                Send Broadcast
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Direct Message Dialog */}
      <Dialog open={showDmDialog} onOpenChange={setShowDmDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <MessageCircle className="w-5 h-5 text-blue-500" /> Message {dmTarget?.name}
            </DialogTitle>
            <DialogDescription>{dmTarget?.email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <Textarea
              value={dmContent}
              onChange={(e) => setDmContent(e.target.value)}
              placeholder="Type your message..."
              className="min-h-[100px]"
              data-testid="dm-content"
            />
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => { setShowDmDialog(false); setDmContent(''); setDmTarget(null); }}>Cancel</Button>
              <Button
                className="flex-1 bg-blue-500 hover:bg-blue-600 text-white"
                disabled={!dmContent.trim() || sendingDm}
                onClick={async () => {
                  setSendingDm(true);
                  try {
                    await adminAPI.directMessage(dmTarget.id, dmContent.trim());
                    toast.success(`Message sent to ${dmTarget.name}`);
                    setShowDmDialog(false);
                    setDmContent('');
                    setDmTarget(null);
                  } catch (err) {
                    toast.error('Failed to send message');
                  } finally {
                    setSendingDm(false);
                  }
                }}
                data-testid="send-dm-btn"
              >
                {sendingDm ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <MessageCircle className="w-4 h-4 mr-1" />}
                Send Message
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
